import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'

// Equipment reads and writes, backed by the database.
//
// Replaces the localStorage getters in data/store.js, which were synchronous
// and returned hardcoded sample equipment when storage was empty — so a failed
// request looked exactly like real records.
//
// The UI and the table disagree on shape, so translation lives here rather than
// being spread through the page:
//   - UI is camelCase, the table is snake_case
//   - UI holds dates as millisecond timestamps, the table stores DATE
//   - the UI carries fields the table has no column for; those go to metadata

export const equipmentKeys = {
  all:  ['equipment'],
  list: (params) => ['equipment', 'list', params ?? {}],
}

// Columns that exist on the table, paired with their UI names.
const COLUMN_MAP = {
  equipment_id:   'equipmentId',
  type:           'type',
  brand:          'brand',
  model:          'model',
  serial_number:  'serialNumber',
  install_date:   'installDate',
  condition:      'condition',
  warranty_type:  'warrantyType',
  warranty_start: 'warrantyStart',
  warranty_end:   'warrantyEnd',
  notes:          'notes',
}

const DATE_COLUMNS = ['install_date', 'warranty_start', 'warranty_end']

const toMs   = (v) => (v ? new Date(v).getTime() : '')
const toDate = (v) => {
  if (v === '' || v === null || v === undefined) return undefined
  const d = new Date(typeof v === 'number' ? v : String(v))
  return isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10)
}

/** Database row -> the shape the Equipment page renders. */
export function toUi(row) {
  if (!row) return row
  const ui = { id: row.id, createdAt: toMs(row.created_at), updatedAt: toMs(row.updated_at) }
  for (const [col, key] of Object.entries(COLUMN_MAP)) {
    ui[key] = DATE_COLUMNS.includes(col) ? toMs(row[col]) : (row[col] ?? '')
  }
  // Everything the table has no column for was stored in metadata
  return {
    serviceCallIds: [], partsHistory: [], conditionHistory: [], documents: [],
    isFlagged: false, flagReason: '',
    ...(row.metadata ?? {}),
    ...ui,
  }
}

/** Page shape -> a request body the equipment route accepts. */
export function toDb(record) {
  const out = {}
  const consumed = new Set(['id', 'createdAt', 'updatedAt'])

  for (const [col, key] of Object.entries(COLUMN_MAP)) {
    consumed.add(key)
    const value = record[key]
    if (value === undefined) continue
    if (DATE_COLUMNS.includes(col)) {
      const d = toDate(value)
      if (d !== undefined) out[col] = d
    } else if (value !== '') {
      out[col] = value
    }
  }

  // Anything left over is preserved rather than silently dropped
  const metadata = {}
  for (const [k, v] of Object.entries(record)) {
    if (!consumed.has(k)) metadata[k] = v
  }
  if (Object.keys(metadata).length) out.metadata = metadata

  return out
}

export function useEquipment(params) {
  return useQuery({
    queryKey: equipmentKeys.list(params),
    queryFn: async () => (await api.getEquipment(params)).data.map(toUi),
  })
}

// Create or update, decided by whether the record already has a server id.
export function useSaveEquipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (record) => {
      const body = toDb(record)
      return record.id ? api.updateEquipment(record.id, body) : api.createEquipment(body)
    },
    // One invalidation keeps every component showing equipment in step — this is
    // what the manual reload() calls used to do by hand.
    onSuccess: () => qc.invalidateQueries({ queryKey: equipmentKeys.all }),
  })
}

export function useDeleteEquipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.deleteEquipment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: equipmentKeys.all }),
  })
}
